// One player in a map: runs the player's movers and pmove each tick, applies
// triggers, falling damage, save/load and the ETJump-style timerun state machine.

import { f, fangleVectors, flength, fscale } from '../f32';
import { copy, type Vec3 } from '../math';
import { BUTTON_ACTIVATE, PMF_TIME_KNOCKBACK, createPlayerState, playerBounds, pmoveSingle, setViewAngles, type PlayerState, type PmoveEvents, type UserCmd } from '../physics/pmove';
import type { PhysicsWorld } from '../world';
import type { GameHooks, MapLogic, Team, TimerKind } from './mapLogic';
import type { MoverContext, MoverSet } from './movers';
import { createStepState, updateMoveSounds, type MoveSound, type StepState } from './moveSounds';
import { createAnimState, updateAnimState, type AnimState } from './movetype';
import type { Replay } from './replay';

export const MAX_HEALTH = 100;
/** ETJump teleporter spawnflags. */
const TELE_RESET_SPEED = 1;
const TELE_CONVERT_SPEED = 2;
const TELE_KNOCKBACK = 16;
const TELE_NO_Z_OFFSET = 32;

export interface Timerun {
  run: string;
  startTime: number;
  checkpoints: (number | null)[];
}

/** Sounds of the player's own actions (not part of the simulation). */
export type SessionSound =
  | MoveSound
  /** trigger_push (at most every 1.5 s, like fly_sound_debounce_time) */
  | { kind: 'jumppad' }
  | { kind: 'teleport' }
  /** A target_speaker was used by this player's trigger chain. */
  | { kind: 'speaker'; entityNum: number };

/** Time between jump pad sounds (ms). */
const FLY_SOUND_DEBOUNCE = 1500;

export type SessionEvent =
  /** restart = the start trigger was touched again during the same run */
  | { type: 'timerStart'; run: string; restart: boolean }
  | { type: 'checkpoint'; run: string; index: number; time: number }
  | { type: 'timerStop'; run: string; time: number; checkpoints: (number | null)[]; replay: Replay | null }
  | { type: 'timerInterrupted'; run: string; reason: string }
  | { type: 'death' }
  | { type: 'saved' }
  | { type: 'loaded' }
  | { type: 'message'; text: string }
  | { type: 'sound'; sound: SessionSound };

interface SavePos {
  origin: Vec3;
  angles: Vec3;
  crouched: boolean;
}

export class PlayerSession implements GameHooks, MoverContext {
  ps: PlayerState;
  /** This player's movers (state and phase follow the player's command clock). */
  readonly movers: MoverSet;
  health = MAX_HEALTH;
  timerun: Timerun | null = null;
  /** What the player model plays (movetype, animation events); not part of the simulation. */
  readonly anim: AnimState = createAnimState();
  /** Footstep cycle (ps->bobCycle); sounds only. */
  readonly steps: StepState = createStepState();
  private flySoundTime = -Infinity;
  /** What the last move did (HUD tools read it after tick()). */
  lastEvents: PmoveEvents | null = null;
  private saves: (SavePos | null)[] = [null, null, null];
  private lastCmd: UserCmd | null = null;
  private listeners: ((e: SessionEvent) => void)[] = [];
  /** While set, events are not emitted and commands are not recorded (prediction re-simulation). */
  muted = false;
  /** Trigger entity -> time it may fire again (per player, like gentity_t timestamps). */
  private triggerCooldowns = new Map<number, number>();
  /** The previous command's buttons (+activate fires on press). */
  oldButtons = 0;
  /** Commands of the current timerun, for the replay. */
  private recording: { start: PlayerState; movers: ReturnType<MoverSet['snapshot']>; buttons: number; cmds: UserCmd[] } | null = null;

  /** Axis (0) or Allies (1): where the player spawns, team only triggers and doors. */
  team: Team;

  /** @param team the player's team (the map's default team when it can not be joined) */
  constructor(
    readonly logic: MapLogic,
    readonly world: PhysicsWorld,
    team?: Team,
  ) {
    this.team = team !== undefined && logic.teams.includes(team) ? team : logic.defaultTeam;
    this.movers = logic.createMovers();
    this.ps = this.spawnState();
  }

  // --- MoverContext ---

  get alive(): boolean {
    return this.health > 0;
  }

  fireTargets(entityNum: number): void {
    this.logic.fireTargets(entityNum, this.ps.commandTime, this);
  }

  on(fn: (e: SessionEvent) => void): void {
    this.listeners.push(fn);
  }

  private emit(e: SessionEvent): void {
    if (this.muted) return;
    for (const fn of this.listeners) fn(e);
  }

  private spawnState(): PlayerState {
    const spawn = this.logic.spawn(this.team);
    const ps = createPlayerState(spawn.origin, spawn.angles[1]);
    ps.gravity = this.logic.gravity;
    if (this.lastCmd) {
      ps.commandTime = this.lastCmd.serverTime;
      setViewAngles(ps, this.lastCmd.angles, spawn.angles);
    } else {
      ps.viewangles = copy(spawn.angles);
    }
    return ps;
  }

  /**
   * Keeps the first command (or one after a long gap) from simulating a huge
   * time step. Client and server both call this before tick() so they agree.
   */
  syncCommandTime(serverTime: number, msec: number): void {
    if (this.ps.commandTime === 0 || serverTime - this.ps.commandTime > 1000) this.ps.commandTime = serverTime - msec;
  }

  /** Runs one movement command and everything that follows from it. */
  tick(cmd: UserCmd): void {
    this.lastCmd = cmd;
    if (this.recording && this.timerun && !this.muted) this.recording.cmds.push({ ...cmd, angles: [cmd.angles[0], cmd.angles[1], cmd.angles[2]], wbuttons: cmd.wbuttons ?? 0 });
    // server frames crossed by this command move the movers (and carry the player) first
    this.movers.advance(cmd.serverTime, this);
    this.movers.apply();
    const events = pmoveSingle(this.ps, cmd, this.world, this.logic.rules);
    this.lastEvents = events;
    updateAnimState(this.anim, this.ps, cmd, events);
    if (!this.muted) for (const sound of updateMoveSounds(this.steps, this.ps, cmd, events)) this.emit({ type: 'sound', sound });
    // ClientImpacts
    for (const ent of events.touched) this.movers.impact(ent, this);
    if (events.fallDamage) {
      // vanilla g_active.c ClientEvents: fall damage knockback is applied after the move
      if (this.logic.rules.mod === 'vanilla' && events.fallDamage < 9999) {
        this.ps.pmTime = events.fallDamage === 25 ? 250 : 1000;
        this.ps.pmFlags |= PMF_TIME_KNOCKBACK;
      }
      this.damage(events.fallDamage);
    }
    if (this.health <= 0) return;
    if (this.ps.pmType === 'normal') {
      const { mins, maxs } = playerBounds(this.ps);
      this.logic.touchTriggers(this.ps.origin, mins, maxs, cmd.serverTime, this, this.triggerCooldowns);
    }
    // +activate fires when pressed (latched_buttons), after the triggers like ClientThink_real
    const pressed = cmd.buttons & ~this.oldButtons;
    this.oldButtons = cmd.buttons;
    if (pressed & BUTTON_ACTIVATE && this.health > 0 && this.ps.pmType === 'normal') {
      const forward: Vec3 = [0, 0, 0];
      fangleVectors(this.ps.viewangles, forward, null, null);
      const eye: Vec3 = [this.ps.origin[0], this.ps.origin[1], this.ps.origin[2] + this.ps.viewheight];
      this.logic.activate(eye, forward, cmd.serverTime, this, this.triggerCooldowns);
    }
  }

  get time(): number {
    return this.ps.commandTime;
  }

  // --- GameHooks ---

  teleport(origin: Vec3, angles: Vec3, spawnflags: number): void {
    const ps = this.ps;
    if (spawnflags & TELE_KNOCKBACK) {
      ps.pmTime = 160;
      ps.pmFlags |= PMF_TIME_KNOCKBACK;
    }
    if (spawnflags & TELE_RESET_SPEED) ps.velocity = [f(0.01), f(0.01), f(0.01)];
    if (spawnflags & TELE_CONVERT_SPEED) {
      const speed = flength(ps.velocity);
      const forward: Vec3 = [0, 0, 0];
      fangleVectors(angles, forward, null, null);
      fscale(ps.velocity, forward, speed);
    }
    ps.origin = [f(origin[0]), f(origin[1]), f(origin[2])];
    if (!(spawnflags & TELE_NO_Z_OFFSET)) ps.origin[2] = f(ps.origin[2] + 1);
    if (this.lastCmd) setViewAngles(ps, this.lastCmd.angles, angles);
    this.emit({ type: 'sound', sound: { kind: 'teleport' } });
  }

  push(velocity: Vec3): void {
    this.ps.velocity = copy(velocity);
    if (this.ps.commandTime >= this.flySoundTime) {
      this.flySoundTime = this.ps.commandTime + FLY_SOUND_DEBOUNCE;
      this.emit({ type: 'sound', sound: { kind: 'jumppad' } });
    }
  }

  speaker(entityNum: number): void {
    this.emit({ type: 'sound', sound: { kind: 'speaker', entityNum } });
  }

  useMover(entityNum: number): void {
    this.movers.use(entityNum, this);
  }

  activateMover(entityNum: number): void {
    this.movers.activate(entityNum, this);
  }

  print(text: string): void {
    this.emit({ type: 'message', text });
  }

  moverTrigger(entityNum: number): void {
    this.movers.triggerTouched(entityNum, this);
  }

  damage(amount: number): void {
    if (this.ps.pmType === 'noclip' || this.health <= 0) return;
    this.health -= amount;
    if (this.health <= 0) this.die();
  }

  timer(kind: TimerKind, run: string, opts: { speedLimit: number; checkpoint: number }): void {
    const t = this.ps.commandTime;
    if (kind === 'start') {
      if (this.ps.pmType !== 'normal') return;
      if (flength(this.ps.velocity) > opts.speedLimit) {
        if (!this.timerun) this.emit({ type: 'message', text: `Too fast to start the run (limit ${opts.speedLimit} ups)` });
        return;
      }
      const checkpoints = new Array(this.logic.runs.get(run)?.checkpoints ?? 0).fill(null);
      const wasRunning = this.timerun?.run === run;
      this.timerun = { run, startTime: t, checkpoints };
      this.recording = { start: structuredClone(this.ps), movers: this.movers.snapshot(), buttons: this.lastCmd?.buttons ?? 0, cmds: [] };
      // other players' mover events during the run go into its replay
      this.movers.keepEventsFrom = this.movers.time;
      this.emit({ type: 'timerStart', run, restart: wasRunning });
      return;
    }
    const tr = this.timerun;
    if (!tr || tr.run !== run) return;
    if (kind === 'checkpoint') {
      if (opts.checkpoint >= 0 && tr.checkpoints[opts.checkpoint] == null) {
        tr.checkpoints[opts.checkpoint] = t - tr.startTime;
        this.emit({ type: 'checkpoint', run, index: opts.checkpoint, time: t - tr.startTime });
      }
      return;
    }
    this.timerun = null;
    const rec = this.recording;
    this.recording = null;
    const from = rec?.movers.time ?? null;
    const moverEvents = rec && from !== null ? this.movers.events.filter((e) => e.frame > from && e.frame <= (this.movers.time ?? from)) : [];
    this.movers.keepEventsFrom = null;
    const replay: Replay | null = rec
      ? { version: 2, msec: rec.cmds[0] ? rec.cmds[0].serverTime - rec.start.commandTime : 8, mode: this.logic.rules.mod, start: rec.start, movers: rec.movers, moverEvents: moverEvents.length ? moverEvents : undefined, buttons: rec.buttons || undefined, team: this.team || undefined, cmds: rec.cmds }
      : null;
    this.emit({ type: 'timerStop', run, time: t - tr.startTime, checkpoints: tr.checkpoints, replay });
  }

  // --- player commands ---

  interrupt(reason: string): void {
    if (!this.timerun) return;
    const run = this.timerun.run;
    this.timerun = null;
    this.recording = null;
    this.movers.keepEventsFrom = null;
    this.emit({ type: 'timerInterrupted', run, reason });
  }

  save(slot = 0): void {
    if (this.health <= 0) return;
    this.saves[slot] = { origin: copy(this.ps.origin), angles: copy(this.ps.viewangles), crouched: (this.ps.pmFlags & 1) !== 0 };
    this.emit({ type: 'saved' });
  }

  hasSave(slot = 0): boolean {
    return !!this.saves[slot];
  }

  load(slot = 0): void {
    const s = this.saves[slot];
    if (!s) {
      this.emit({ type: 'message', text: 'No saved position' });
      return;
    }
    this.interrupt('load');
    const keepTime = this.ps.commandTime;
    this.ps = this.spawnState();
    this.ps.commandTime = keepTime;
    this.ps.origin = copy(s.origin);
    if (s.crouched) this.ps.pmFlags |= 1;
    if (this.lastCmd) setViewAngles(this.ps, this.lastCmd.angles, s.angles);
    this.health = MAX_HEALTH;
    this.triggerCooldowns.clear();
    this.emit({ type: 'loaded' });
  }

  /**
   * Joins a team (one the map has spawns for): the run stops and the player
   * respawns at the team's spawn. Returns false when the team can not be joined.
   */
  setTeam(team: Team): boolean {
    if (!this.logic.teams.includes(team)) return false;
    this.interrupt('team change');
    this.team = team;
    this.respawn();
    return true;
  }

  respawn(): void {
    this.interrupt('respawn');
    const keepTime = this.ps.commandTime;
    this.ps = this.spawnState();
    this.ps.commandTime = keepTime;
    this.health = MAX_HEALTH;
    this.triggerCooldowns.clear();
  }

  private die(): void {
    this.emit({ type: 'death' });
    this.interrupt('death');
    if (this.saves[0]) this.load(0);
    else this.respawn();
  }

  toggleNoclip(): void {
    this.interrupt('noclip');
    this.ps.pmType = this.ps.pmType === 'noclip' ? 'normal' : 'noclip';
    this.ps.velocity = [0, 0, 0];
    this.emit({ type: 'message', text: this.ps.pmType === 'noclip' ? 'noclip ON' : 'noclip OFF' });
  }
}
